/**
 * One address, written the way a maps app expects it.
 *
 * A street number without a town is not an address: "St. Canisiussingel 20" exists in more than one
 * place, and handing that to Google Maps or Apple Maps sends someone to whichever one the search
 * happens to rank first. Both the project screen and the shift screen build this string before
 * opening navigation, so it lives in one place with the combinations pinned.
 */

export type AddressParts = {
  addressLine?: string | null;
  zipCode?: string | null;
  city?: string | null;
  state?: string | null;
};

/**
 * Returns the address as a single line, or an empty string when there is no street to go to.
 *
 * Zip code and city belong together on one part ("6511 AB Nijmegen"); either one alone still tells
 * the map which town is meant, so it is kept. Blank strings count as missing — a planner leaving a
 * field empty must not produce a stray comma.
 */
export function formatFullAddress({ addressLine, zipCode, city, state }: AddressParts): string {
  const street = addressLine?.trim();

  if (!street) {
    return "";
  }

  const zip = zipCode?.trim();
  const town = city?.trim();
  const region = state?.trim();

  const parts = [street];

  if (zip && town) {
    parts.push(`${zip} ${town}`);
  } else if (town || zip) {
    parts.push(town || zip || "");
  }

  if (region) {
    parts.push(region);
  }

  return parts.join(", ");
}
